import type { SourceEnum } from 'js/src/client';
import { AssetContainerTypes, AssetMaxDimensions, AssetMinDimensions } from 'js/src/client';
import { shouldEnableAllPages } from 'js/src/data/clip-creator/clip-creator-utils';
import {
    ClipCreatorPage,
    disablePage,
    enablePage,
    getSelectedPage,
    setSelectedPage,
} from 'js/src/data/clip-creator/slices/clip-creator-pages-slice';
import type { ClipPreviewModalContext } from 'js/src/data/modals';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import { getTranscriptsToSave } from 'js/src/data/transcript';
import type { UploadOptions } from 'js/src/data/uploads';
import {
    createUploadSession,
    getUploadedAssetByUuid,
    UploadsOperations,
} from 'js/src/data/uploads';
import ClipCreatorEventEmitter, {
    CLIP_CREATOR_EVENTS,
} from 'js/src/libs/clip-creator-event-emitter';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';

export const uploadFileAction = (file: File, source?: SourceEnum) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const files = [file];
        const uploadSessionId = uuid();
        dispatch(createUploadSession({ uploadSessionId }));

        const isAudioFile = file.type.startsWith('audio/');
        const assetType = isAudioFile ? AssetContainerTypes.AUDIO : AssetContainerTypes.BACKGROUND;

        const uploadOpts: UploadOptions = {
            asset_type: assetType,
            only_images: false, // to only allow video file types
            add_to_user_library: false, // should be false unless we use the UploadMediaController directly - currently, we use it via the upload-operations
            min_width: isAudioFile ? undefined : AssetMinDimensions.MIN_USER_UPLOAD_PIXELS,
            min_height: isAudioFile ? undefined : AssetMinDimensions.MIN_USER_UPLOAD_PIXELS,
            max_width: isAudioFile ? undefined : AssetMaxDimensions.MAX_PIXELS,
            max_height: isAudioFile ? undefined : AssetMaxDimensions.MAX_PIXELS,
            metadata: { from_clip_creator: true, ...(source !== undefined && { source }) },
            file_list: files.map((file) => ({ file })),
            analyticsContext: {
                trigger: 'Clip creator',
            },
        };
        const reduxIds = await dispatch(UploadsOperations.startUpload(uploadSessionId, uploadOpts));
        if (reduxIds.length === 0) {
            throw new Error('Could not save uploaded asset to redux');
        }
        const reduxId = reduxIds[0];
        const uploadedAsset = getUploadedAssetByUuid(getState(), reduxId);
        if (!uploadedAsset) {
            throw new Error('Uploaded asset not found in redux');
        }
        return uploadedAsset;
    };
};

export const getTranscriptsToSaveForClipAction = (sceneGroupId: string) => {
    return (_: AppDispatch, getState: GetRootState): ReturnType<typeof getTranscriptsToSave> => {
        const transcripts = getTranscriptsToSave(getState());
        return {
            [sceneGroupId]: transcripts[sceneGroupId],
        };
    };
};

const shouldEnableUploadPageOperation = () => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const enableAllPages = shouldEnableAllPages();
        const page = getSelectedPage(getState());
        const isAllowedPage = page === ClipCreatorPage.Upload;
        const enableUploadPage = isAllowedPage || enableAllPages;
        if (enableUploadPage) {
            dispatch(enablePage({ id: ClipCreatorPage.Upload }));
        } else {
            dispatch(disablePage({ id: ClipCreatorPage.Upload }));
        }
    };
};

const shouldEnableCreatePageOperation = () => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const enableAllPages = shouldEnableAllPages();
        const page = getSelectedPage(getState());
        const isAllowedPage = page === ClipCreatorPage.Review || enableAllPages;
        if (isAllowedPage) {
            dispatch(enablePage({ id: ClipCreatorPage.Review }));
        } else {
            dispatch(disablePage({ id: ClipCreatorPage.Review }));
        }
    };
};

export const updateNavigation = () => {
    return (dispatch: AppDispatch) => {
        dispatch(shouldEnableUploadPageOperation());
        dispatch(shouldEnableCreatePageOperation());
    };
};

export const navigateTo = (nextPage: ClipCreatorPage) => {
    return (dispatch: AppDispatch) => {
        dispatch(setSelectedPage(nextPage));
        dispatch(updateNavigation());
    };
};

export const openClipPreview = (clipId: number) => {
    return (dispatch: AppDispatch) => {
        ClipCreatorEventEmitter.emit(CLIP_CREATOR_EVENTS.STOP_PREVIEW);
        dispatch(
            showModal({
                modal_id: MODAL_IDS.CLIP_PREVIEW,
                modal_context: ImmutableFromJS<ClipPreviewModalContext>({
                    clipId,
                }),
            }),
        );
    };
};
